import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import * as projectApi from '../services/projectApi.js';
import * as o2kApi from '../services/projectO2kApi.js';
import { useGraphStore } from './graphStore.js';

/**
 * Analysis store — open files, per-chamber raw data, O2-kinetics transitions,
 * and workspace state. Replaces the hardcoded A/B pattern in fileDataStore
 * with N-chamber support.
 *
 * Graph settings (scaling, traces, advanced flags, extra graphs) live in a
 * separate graphStore. This store keeps the raw data only; graphStore reads
 * it via analysisStore.getState() in its seed action.
 *
 * Structure:
 *   openFiles: { [tabId]: FileTab }
 *   FileTab: { projectId, filename, displayName, fileMeta, chambers,
 *              deviceSeries, activeWorkspace, activeChamberIndex }
 *   chambers: { [chamberIndex]: ChamberState }
 *   ChamberState: { data, marks, events, plotConfig, transitions,
 *                   selectedTransitionId, ... }
 *   deviceSeries: { [dataType]: { type, data, time } }  — chamberless raw
 *                   signals (e.g. CoreTemperature)
 */

let tabCounter = 0;
function generateTabId() {
  return `tab-${Date.now()}-${++tabCounter}`;
}

// Default fit parameters for O2 kinetics
const DEFAULT_FIT_PARAMS = {
  fit_method: 'SHIFTED_HYPERBOLIC_FUNCTION',
  tau: 2.3,
  upper_bound: 10,
  zero_flux_thresh: 3.5,
  outlier_thresh_sigma: 5.9,
  smoothing_points: 200,
  smoothing_poly_order: 1,
};

function emptyChamberState() {
  return {
    data: null, // { timeSeries, oxygenSeries, fluxSeries, dataFrequency, fileName }
    marks: [],
    events: [],
    plotConfig: [],
    transitions: [],
    selectedTransitionId: null,
    transitionsLocked: false,
    transitionsLoading: false,
    o2kineticsError: null,
  };
}

// ── Transition helpers (same logic as fileDataStore, generalized) ──────

function mapBackendTransitionToStore(t) {
  try {
    const m = t?.transition_mark || {};
    const start = new Date((m.time_start ?? 0) * 1000);
    const end = new Date((m.time_end ?? m.time_start ?? 0) * 1000);
    return {
      id: m.id,
      name: m.name || 'transition',
      start,
      end,
      n_points: m.n_points ?? undefined,
      status: 'remote',
      isDirty: false,
      fitParameter: t?.fit_parameter ? { ...t.fit_parameter } : undefined,
      result: t?.result ? { ...t.result } : null,
    };
  } catch {
    return null;
  }
}

function mapStoreTransitionToBackend(tr) {
  const startMs = (tr.start instanceof Date ? tr.start : new Date(tr.start)).getTime();
  const endMs = (tr.end instanceof Date ? tr.end : new Date(tr.end)).getTime();
  const time_start = Math.min(startMs, endMs) / 1000;
  const time_end = Math.max(startMs, endMs) / 1000;
  return {
    transition_mark: {
      id: tr.id,
      mark_type: 'TIME_RANGE',
      mark_signal_type: null,
      name: tr.name || 'transition',
      time_start,
      time_end,
      n_points: typeof tr.n_points === 'number' ? tr.n_points : 0,
    },
    fit_parameter: tr.fitParameter,
    result: tr.result ?? undefined,
  };
}

function computeTransitionPoints(chamberData, start, end) {
  if (!chamberData?.timeSeries || !Array.isArray(chamberData.timeSeries)) return undefined;
  try {
    const startSec = start instanceof Date ? start.getTime() / 1000 : Number(start);
    const endSec = end instanceof Date ? end.getTime() / 1000 : Number(end);
    if (!Number.isFinite(startSec) || !Number.isFinite(endSec)) return undefined;
    const lo = Math.min(startSec, endSec);
    const hi = Math.max(startSec, endSec);
    let count = 0;
    for (let i = 0; i < chamberData.timeSeries.length; i++) {
      if (chamberData.timeSeries[i] >= lo && chamberData.timeSeries[i] <= hi) count++;
    }
    return count;
  } catch {
    return undefined;
  }
}

function transformDld8Data(apiResponse, fileMeta = null) {
  if (!apiResponse?.data_series) return null;
  const timeSeries = apiResponse.data_series.find((s) => s.type === 'Time');
  const oxygen = apiResponse.data_series.find((s) => s.type === 'O2');
  const flux = apiResponse.data_series.find((s) => s.type === 'O2Flux');
  if (!timeSeries || !oxygen) return null;
  const result = {
    timeSeries: timeSeries.data,
    oxygenSeries: oxygen.data,
    dataFrequency: fileMeta?.data_frequency || 0.1,
    fileName: fileMeta?.file_name || 'Unknown File',
  };
  if (flux) result.fluxSeries = flux.data;
  return result;
}

// ── Store ─────────────────────────────────────────────────────────────

export const useAnalysisStore = create(
  subscribeWithSelector((set, get) => ({
    // Current project context (one project at a time in Analysis)
    currentProjectId: null,

    // Open files, keyed by tabId
    openFiles: {},

    // Which tab is active
    activeTabId: null,

    // Global loading/error for file open operations
    isLoading: false,
    error: null,

    // ── Tab management ────────────────────────────────────────────────

    openFile: (projectId, filename, displayName) => {
      // Check if file is already open
      const { openFiles } = get();
      const existing = Object.entries(openFiles).find(
        ([, tab]) => tab.projectId === projectId && tab.filename === filename,
      );
      if (existing) {
        set({ activeTabId: existing[0], currentProjectId: projectId });
        return existing[0];
      }

      const tabId = generateTabId();
      set((state) => ({
        currentProjectId: projectId,
        activeTabId: tabId,
        isLoading: true,
        error: null,
        openFiles: {
          ...state.openFiles,
          [tabId]: {
            projectId,
            filename,
            displayName: displayName || filename,
            fileMeta: null,
            chambers: {},
            deviceSeries: {},
            activeWorkspace: 'rawData',
            activeChamberIndex: 1,
            // activeGraphId tracks the currently focused graph (chamber OR
            // extra). For chamber graphs it mirrors `chamber-{N}`; for extras
            // it holds the graph's generated id.
            activeGraphId: 'chamber-1',
          },
        },
      }));

      // Load data in background — don't block the caller
      get()._loadFileData(tabId, projectId, filename);

      return tabId;
    },

    // Internal: async data loading for a tab (called by openFile)
    // Uses the bulk endpoint — one HTTP request for all chambers' data
    _loadFileData: async (tabId, projectId, filename) => {
      try {
        const bulkData = await projectApi.getFileBulkData(projectId, filename);
        const chambers = {};

        for (const ch of bulkData.chambers || []) {
          const data = transformDld8Data({ data_series: ch.data_series }, { file_name: filename });
          // All marks pass through; ChamberPlotView filters per the
          // currently-selected trace's channel at render time.
          const marks = Array.isArray(ch.marks)
            ? ch.marks.map((m) => ({
                ...m,
                start: m.time_start ?? m.start,
                end: m.time_end ?? m.end,
              }))
            : [];
          if (data) {
            chambers[ch.chamber] = {
              ...emptyChamberState(),
              data,
              marks,
              events: Array.isArray(ch.events) ? ch.events : [],
              plotConfig: Array.isArray(ch.plot_config) ? ch.plot_config : [],
            };
          }
        }

        const deviceSeries = {};
        for (const ds of bulkData.device_series || []) {
          if (ds?.type) deviceSeries[ds.type] = ds;
        }

        set((state) => ({
          isLoading: false,
          openFiles: {
            ...state.openFiles,
            [tabId]: {
              ...state.openFiles[tabId],
              fileMeta: { chamber_count: (bulkData.chambers || []).length },
              chambers,
              deviceSeries,
            },
          },
        }));

        // Seed default Graphs now that chambers + deviceSeries are populated.
        useGraphStore.getState().seedGraphsForTab(tabId);
      } catch (e) {
        set({ isLoading: false, error: e.response?.data?.detail || e.message });
      }
    },

    closeFile: (tabId) => {
      set((state) => {
        const { [tabId]: removed, ...rest } = state.openFiles;
        const newActiveTab =
          state.activeTabId === tabId ? Object.keys(rest)[0] || null : state.activeTabId;
        return { openFiles: rest, activeTabId: newActiveTab };
      });
      useGraphStore.getState().clearGraphsForTab(tabId);
    },

    setActiveTab: (tabId) => set({ activeTabId: tabId }),

    setActiveWorkspace: (tabId, workspace) => {
      set((state) => {
        const tab = state.openFiles[tabId];
        if (!tab) return state;
        return {
          openFiles: {
            ...state.openFiles,
            [tabId]: { ...tab, activeWorkspace: workspace },
          },
        };
      });
    },

    setActiveChamber: (tabId, chamberIndex) => {
      set((state) => {
        const tab = state.openFiles[tabId];
        if (!tab) return state;
        return {
          openFiles: {
            ...state.openFiles,
            [tabId]: {
              ...tab,
              activeChamberIndex: chamberIndex,
              activeGraphId: `chamber-${chamberIndex}`,
            },
          },
        };
      });
    },

    // Activate an arbitrary graph (chamber or extra) by id. For chamber
    // graphs prefer setActiveChamber so activeChamberIndex stays in sync.
    setActiveGraph: (tabId, graphId) => {
      set((state) => {
        const tab = state.openFiles[tabId];
        if (!tab) return state;
        return {
          openFiles: {
            ...state.openFiles,
            [tabId]: { ...tab, activeGraphId: graphId, activeChamberIndex: null },
          },
        };
      });
    },

    // ── Chamber data helpers ──────────────────────────────────────────

    _getChamber: (tabId, chamberIdx) => {
      const tab = get().openFiles[tabId];
      return tab?.chambers?.[chamberIdx] || null;
    },

    _updateChamber: (tabId, chamberIdx, updater) => {
      set((state) => {
        const tab = state.openFiles[tabId];
        if (!tab) return state;
        const chamber = tab.chambers[chamberIdx];
        if (!chamber) return state;
        const updated =
          typeof updater === 'function' ? updater(chamber) : { ...chamber, ...updater };
        return {
          openFiles: {
            ...state.openFiles,
            [tabId]: {
              ...tab,
              chambers: { ...tab.chambers, [chamberIdx]: updated },
            },
          },
        };
      });
    },

    // ── Transition actions (parameterized by tabId + chamberIdx) ──────

    setTransitionsLocked: (tabId, chamberIdx, locked) => {
      get()._updateChamber(tabId, chamberIdx, { transitionsLocked: !!locked });
    },

    setSelectedTransition: (tabId, chamberIdx, id) => {
      get()._updateChamber(tabId, chamberIdx, { selectedTransitionId: id });
    },

    addTransition: (tabId, chamberIdx, transition) => {
      const chamber = get()._getChamber(tabId, chamberIdx);
      if (!chamber) return;
      const npts = computeTransitionPoints(chamber.data, transition.start, transition.end);
      const finalT = {
        ...transition,
        n_points: npts,
        status: transition.status || 'local',
        isDirty: true,
      };
      get()._updateChamber(tabId, chamberIdx, (c) => ({
        ...c,
        transitions: [...c.transitions, finalT],
      }));
    },

    updateTransition: (tabId, chamberIdx, id, updater) => {
      get()._updateChamber(tabId, chamberIdx, (c) => ({
        ...c,
        transitions: c.transitions.map((t) => {
          if (t.id !== id) return t;
          const next = typeof updater === 'function' ? updater(t) : { ...t, ...updater };
          if (next?.start && next?.end) {
            next.n_points = computeTransitionPoints(c.data, next.start, next.end);
          }
          if (t.status === 'remote') next.isDirty = true;
          return next;
        }),
      }));
    },

    removeTransition: (tabId, chamberIdx, id) => {
      get()._updateChamber(tabId, chamberIdx, (c) => ({
        ...c,
        transitions: c.transitions.filter((t) => t.id !== id),
        selectedTransitionId: c.selectedTransitionId === id ? null : c.selectedTransitionId,
      }));
    },

    replaceTransitionId: (tabId, chamberIdx, oldId, newId) => {
      get()._updateChamber(tabId, chamberIdx, (c) => ({
        ...c,
        transitions: c.transitions.map((t) =>
          t.id === oldId ? { ...t, id: newId, status: 'remote' } : t,
        ),
        selectedTransitionId: c.selectedTransitionId === oldId ? newId : c.selectedTransitionId,
      }));
    },

    setFitParameters: (tabId, chamberIdx, id, params) => {
      get()._updateChamber(tabId, chamberIdx, (c) => ({
        ...c,
        transitions: c.transitions.map((t) => {
          if (t.id !== id) return t;
          const updated = { ...t, fitParameter: { ...(t.fitParameter || {}), ...params } };
          if (t.status === 'remote') updated.isDirty = true;
          return updated;
        }),
      }));
    },

    // Async: fetch transitions for a chamber from the server
    fetchTransitions: async (tabId, chamberIdx) => {
      const tab = get().openFiles[tabId];
      if (!tab) return;
      const chamberParam = `chamber${chamberIdx}`;
      get()._updateChamber(tabId, chamberIdx, { transitionsLoading: true });
      try {
        const list = await o2kApi.listTransitions(tab.projectId, tab.filename, chamberParam);
        const incoming = (Array.isArray(list) ? list : [])
          .map(mapBackendTransitionToStore)
          .filter(Boolean);
        get()._updateChamber(tabId, chamberIdx, (c) => {
          const byId = new Map(c.transitions.map((t) => [t.id, t]));
          for (const remote of incoming) {
            const prev = byId.get(remote.id);
            if (prev && prev.isDirty) {
              byId.set(remote.id, { ...prev, status: 'remote' });
            } else {
              byId.set(remote.id, { ...(prev || {}), ...remote, status: 'remote', isDirty: false });
            }
          }
          return { ...c, transitions: Array.from(byId.values()), transitionsLoading: false };
        });
      } catch (e) {
        get()._updateChamber(tabId, chamberIdx, {
          transitionsLoading: false,
          o2kineticsError: e.response?.data?.detail || e.message,
        });
      }
    },

    // Async: fit selected transition
    fitSelectedTransition: async (tabId, chamberIdx) => {
      const tab = get().openFiles[tabId];
      const chamber = get()._getChamber(tabId, chamberIdx);
      if (!tab || !chamber) return;

      get()._updateChamber(tabId, chamberIdx, { o2kineticsError: null });

      const tr = chamber.transitions.find((t) => t.id === chamber.selectedTransitionId);
      if (!tr) return;

      const npts = computeTransitionPoints(chamber.data, tr.start, tr.end);
      const effectiveFit = { ...DEFAULT_FIT_PARAMS, ...(tr.fitParameter || {}) };
      // Coerce numeric types
      effectiveFit.tau = Number(effectiveFit.tau);
      effectiveFit.upper_bound = Number(effectiveFit.upper_bound);
      effectiveFit.zero_flux_thresh = Number(effectiveFit.zero_flux_thresh);
      effectiveFit.outlier_thresh_sigma = Number(effectiveFit.outlier_thresh_sigma);
      effectiveFit.smoothing_points = parseInt(effectiveFit.smoothing_points, 10);
      effectiveFit.smoothing_poly_order = parseInt(effectiveFit.smoothing_poly_order, 10);

      const payload = mapStoreTransitionToBackend({
        ...tr,
        n_points: npts,
        fitParameter: effectiveFit,
      });
      const chamberParam = `chamber${chamberIdx}`;
      const isRemote = tr.status === 'remote';

      try {
        const res = isRemote
          ? await o2kApi.updateTransition(tab.projectId, tab.filename, chamberParam, payload)
          : await o2kApi.createTransition(tab.projectId, tab.filename, chamberParam, payload);

        const mapped = mapBackendTransitionToStore(res);
        if (mapped) {
          const savedId = mapped.id || tr.id;
          get().updateTransition(tabId, chamberIdx, tr.id, {
            name: mapped.name ?? tr.name,
            start: mapped.start ?? tr.start,
            end: mapped.end ?? tr.end,
            n_points: typeof mapped.n_points === 'number' ? mapped.n_points : npts,
            fitParameter: mapped.fitParameter ?? tr.fitParameter,
            result: mapped.result ?? null,
            status: 'remote',
            isDirty: false,
          });
          if (!isRemote && mapped.id && mapped.id !== tr.id) {
            get().replaceTransitionId(tabId, chamberIdx, tr.id, mapped.id);
          }
          // Refresh from server
          await get().fetchTransitions(tabId, chamberIdx);
          get().setSelectedTransition(tabId, chamberIdx, savedId);
        }
      } catch (e) {
        let errorMessage = 'Fit failed';
        const detail = e?.response?.data?.detail;
        if (typeof detail === 'string') errorMessage = detail;
        else if (Array.isArray(detail) && detail.length > 0) {
          errorMessage = detail
            .map(
              (d) =>
                `${Array.isArray(d.loc) ? d.loc.join('.') : ''}: ${d.msg || 'Validation error'}`,
            )
            .join('; ');
        } else if (e?.message) errorMessage = e.message;
        get()._updateChamber(tabId, chamberIdx, { o2kineticsError: errorMessage });
      }
    },

    // Async: delete transition
    deleteTransition: async (tabId, chamberIdx, id) => {
      const tab = get().openFiles[tabId];
      const chamber = get()._getChamber(tabId, chamberIdx);
      if (!tab || !chamber) return;

      const tr = chamber.transitions.find((t) => t.id === id);
      if (!tr) return;

      try {
        if (tr.status === 'remote') {
          const chamberParam = `chamber${chamberIdx}`;
          await o2kApi.deleteTransition(tab.projectId, tab.filename, chamberParam, id);
        }
        get().removeTransition(tabId, chamberIdx, id);
        await get().fetchTransitions(tabId, chamberIdx);
      } catch (e) {
        get()._updateChamber(tabId, chamberIdx, {
          o2kineticsError: e.response?.data?.detail || e.message,
        });
      }
    },

    clearError: () => set({ error: null }),
  })),
);
